import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container, Box, TextField, InputAdornment, CircularProgress, Stack,
  ToggleButton, ToggleButtonGroup, Button, Chip,
} from '@mui/material';
import { Search as SearchIcon, Download as DownloadIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import fundedAPI, { downloadLedgerCSV } from '../../api/funded';
import FundedNav from './FundedNav';
import FundingTable from '../../components/funded/FundingTable';

export default function LedgerView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [type, setType] = useState('fundings');
  const [search, setSearch] = useState('');
  const [lender, setLender] = useState(searchParams.get('lender') || '');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, pages: 1, sumAmount: 0 });
  const [loading, setLoading] = useState(true);

  const params = useCallback(() => ({
    type, search, lender, dateFrom, dateTo, minAmount, maxAmount, sortBy, order,
  }), [type, search, lender, dateFrom, dateTo, minAmount, maxAmount, sortBy, order]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fundedAPI.getLedger({ ...params(), page, limit: 50 });
      setRows(res.data.data || []);
      setMeta({ total: res.data.total, page: res.data.page, pages: res.data.pages, sumAmount: res.data.sumAmount });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [params, page]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const handleSort = (col) => {
    if (sortBy === col) setOrder(order === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setOrder('desc'); }
    setPage(1);
  };

  const handleExport = async () => {
    try {
      await downloadLedgerCSV(params());
      toast.success('Export downloaded');
    } catch {
      toast.error('Export failed');
    }
  };

  const clearLender = () => {
    setLender('');
    searchParams.delete('lender');
    setSearchParams(searchParams, { replace: true });
    setPage(1);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <FundedNav
        title="Fundings & Commissions"
        subtitle="All ACH-level events"
        actions={<Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExport}>Export CSV</Button>}
      />

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap" useFlexGap>
        <ToggleButtonGroup value={type} exclusive size="small" onChange={(_, v) => { if (v) { setType(v); setPage(1); } }}>
          <ToggleButton value="fundings">Fundings</ToggleButton>
          <ToggleButton value="commissions">Commissions</ToggleButton>
        </ToggleButtonGroup>
        {lender && <Chip label={`Lender: ${lender}`} onDelete={clearLender} size="small" color="primary" />}
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <TextField
          size="small" placeholder="Search client, lender, deposit…"
          value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          sx={{ width: { xs: '100%', sm: 260 } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <TextField size="small" label="From" type="date" InputLabelProps={{ shrink: true }} value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} />
        <TextField size="small" label="To" type="date" InputLabelProps={{ shrink: true }} value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} />
        <TextField size="small" label="Min $" type="number" value={minAmount} onChange={(e) => { setMinAmount(e.target.value); setPage(1); }} sx={{ width: 110 }} />
        <TextField size="small" label="Max $" type="number" value={maxAmount} onChange={(e) => { setMaxAmount(e.target.value); setPage(1); }} sx={{ width: 110 }} />
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      ) : (
        <FundingTable
          rows={rows}
          showClient
          sortBy={sortBy}
          order={order}
          onSort={handleSort}
          total={meta.total}
          page={meta.page}
          pages={meta.pages}
          sumAmount={meta.sumAmount}
          onPageChange={setPage}
          amountLabel={type === 'commissions' ? 'Commission' : 'Amount'}
        />
      )}
    </Container>
  );
}
